<template>
  <div class="file-manager-wrapper">
    <div class="project-design-section">      <!-- Show full page header when used as standalone component, simplified header when embedded -->
      <AppPageHeader v-if="!props.projectId" :title="$t('design.title')" :subtitle="$t('design.subtitle')">
        <template #actions>
          <AppButton variant="primary" size="medium" @click="handleNewDesignClick" :disabled="loading">
            <span class="btn-icon">+</span> {{ $t('design.newDesign') }}
          </AppButton>
        </template>
      </AppPageHeader>      <!-- Simplified header when embedded in project details -->
      <div v-else class="embedded-header">
        <h2 class="component-title"></h2>
        <div class="header-actions">
          <AppButton variant="primary" size="medium" @click="handleNewDesignClick" :disabled="loading">
            <span class="btn-icon">+</span> {{ $t('design.newDesign') }}
          </AppButton>
        </div>
      </div>
      
      <!-- Project context display when coming from a project (but not when already in project details) -->
      <div v-if="sourceProject && !props.projectId" class="project-context">
        <div class="project-context-content">
          <div class="project-info">
            <h3>{{ $t('design.projectFiles', { projectName: sourceProject.name }) }}</h3>
            <p>{{ $t('design.projectFilesDescription') }}</p>
          </div>
          <AppButton variant="outline" size="small" @click="returnToProject" class="return-to-project-btn">
            <span class="btn-icon">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
              </svg>
            </span>
            {{ $t('design.returnToProject') }}
          </AppButton>
        </div>
      </div>

      <AppCard class="mt-lg">
        <div v-if="loading" class="loading-indicator">
          <div class="spinner"></div>
          <p>{{ $t('common.loading') }}</p>
        </div>

        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
          <AppButton @click="fetchFiles" variant="outline" size="small">{{ $t('common.retry') }}</AppButton>
        </div>

        <div v-else-if="files.length === 0" class="empty-state">
          <i class="pi pi-file" style="font-size: 3rem"></i>
          <p>{{ $t('design.noFiles') }}</p>
        </div>

        <div v-else class="files-container">         
          <div class="grid-wrapper">            
          <AppDataGrid
              :columnDefs="columnDefs" 
              :rowData="filteredFiles"
              :defaultColDef="defaultColDef" 
              :pagination="true"
              :pageSize="20"
              :pageSizeOptions="[5, 10, 25, 50, 100]"
              :suppressPaginationPanel="false"
              :animateRows="true" 
              :showSearch="true"
              :searchPlaceholder="$t('design.searchFiles')"
              @grid-ready="onGridReady"
              @action-clicked="handleAction"
              :suppressMovableColumns="false" 
              gridStyle="width: 100%;"
              domLayout="autoHeight"
              :actions="actions"
            />
          </div>
        </div>
      </AppCard>      <!-- Delete Confirmation Modal -->      
      <transition name="fade">
        <DeleteConfirmationModal
          v-if="showDeleteConfirmation"          
          :filename="fileToDelete?.fileName || ''"
          :doc-type="fileToDelete?.designType || ''"
          :description="fileToDelete?.description || ''"
          :loading="loading"
          @confirm="confirmDelete"
          @cancel="cancelDelete"
        />
      </transition>      <!-- New Design Modal -->
      <NewDesignModal
        :show="showNewDesignModal"
        :project-id="props.projectId"
        @created="handleDesignCreated"
        @close="handleNewDesignModalClose"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from '@/composables/useStore';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import DesignService from '@/services/design.service';
import type { UploadedFile } from '@/types';

import AppCard from '../../base/AppCard.vue';
import AppButton from '../../base/AppButton.vue';
import AppPageHeader from '../../base/AppPageHeader.vue';
import AppDataGrid from '../../base/AppDataGrid.vue';
import DeleteConfirmationModal from '../documents/DeleteConfirmationModal.vue';
import NewDesignModal from './NewDesignModal.vue';

// Define props for when used as a component in DynamicContent
interface Props {
  projectId?: string | number | null;
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null
});

// Emits
interface Emits {
  (e: 'design-selected', design: UploadedFile): void;
}

const emit = defineEmits<Emits>();

const store = useStore();
const { t } = useI18n();
const router = useRouter();

// Data
const files = ref<any[]>([]);
const showDeleteConfirmation = ref(false);
const fileToDelete = ref<any>(null);
const sourceProject = ref<any>(null);
const gridApi = ref<any>(null);
const gridColumnApi = ref<any>(null);
const error = ref<string | null>(null);
const loading = ref(false);
const isInitialLoad = ref(true);
const selectedDesignType = ref('all');
const showNewDesignModal = ref(false);

const filteredFiles = computed(() => {
  let result = files.value;
  
  // Filter by design type
  if (selectedDesignType.value !== 'all') {
    result = result.filter((file: any) => file.designType === selectedDesignType.value);
  }
  
  return result;
});

const getFileIcon = (filename: string) => {
  if (!filename) return '';

  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, string> = {
    'pdf': '<i class="fas fa-file-pdf" style="color: #e74c3c;"></i>',
    'doc': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'docx': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'dwg': '<i class="fas fa-drafting-compass" style="color: #27ae60;"></i>',
    'dxf': '<i class="fas fa-drafting-compass" style="color: #27ae60;"></i>',
    'jpg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'jpeg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'png': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'gif': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'bmp': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'tiff': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'zip': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
    'rar': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
  };

  return extension ? (iconMap[extension] || '<i class="fas fa-file" style="color: #95a5a6;"></i>') : '';
};

// Grid Configuration - make reactive to language changes
const columnDefs = computed(() => [
  {
    headerName: '#',
    field: 'rowNumber',
    valueGetter: (params: any) => params.node.rowIndex + 1,
    width: 70,
    pinned: 'left',
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    field: 'fileType',
    headerName: '',
    width: 60,
    cellRenderer: (params: any) => {
      const span = document.createElement('span');
      span.innerHTML = getFileIcon(params.data.fileName);
      return span;
    },
    resizable: false,
    sortable: false,
    filter: false
  },
  {
    field: 'fileName',
    headerName: t('design.filename'),
    type: 'text',
    sortable: true,
    resizable: true,
    flex: 1,
    minWidth: 150
  },
  {
    field: 'designType',
    headerName: t('design.designType'),
    type: 'text',
    sortable: true,
    resizable: true,
    minWidth: 100
  },
  {
    field: 'createdAt',
    headerName: t('design.uploadDate'),
    type: 'date',
    sortable: true,
    resizable: true,
    minWidth: 180
  }
]);

// Actions configuration for AppDataGrid (dynamic based on file type)
const actions = computed(() => [
  {
    name: 'open',
    icon: 'fas fa-external-link-alt',
    label: t('design.openInDesigner'),
    tooltip: t('design.openInDesigner'),
    class: 'open-btn',
    // Dynamic action based on file type
    getLabel: (data: any) => {
      const fileName = data.fileName || '';
      const isDesignFile = fileName.toLowerCase().endsWith('.sh3d') || fileName.toLowerCase().endsWith('.sh3x');
      return isDesignFile ? t('design.openInDesigner') : t('design.download');
    },
    getIcon: (data: any) => {
      const fileName = data.fileName || '';
      const isDesignFile = fileName.toLowerCase().endsWith('.sh3d') || fileName.toLowerCase().endsWith('.sh3x');
      return isDesignFile ? 'fas fa-external-link-alt' : 'fas fa-download';
    }
  },
  ({
    name: 'delete',
    icon: 'fas fa-trash',
    label: t('design.delete'),
    tooltip: t('design.delete'),
    class: 'delete-btn',
    // Only allow delete when the creator (createdBy) matches the current user
    condition: (data: any) => {
      try {
        const user = store.getters.getUser.value;
        if (!user) return false;
        const uid = String(user.id || '');
        return data?.createdBy && String(data.createdBy) === uid;
      } catch (e) {
        return false;
      }
    }
  } as any)
]);

const defaultColDef = {
  resizable: true,
  wrapText: false,
  autoHeight: false,
};

// Fetch files
const fetchFiles = async (force = false) => {
  if (!props.projectId) {
    error.value = t('notifications.selectProjectFirst');
    files.value = [];
    return;
  }

  // Prevent double loading unless forced
  if (loading.value || (!force && !isInitialLoad.value)) {
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await DesignService.getProjectDesignFiles(props.projectId);
    files.value = response || [];
    isInitialLoad.value = false;
  } catch (err: any) {
    console.error('Error fetching design files:', err);
    error.value = err.message || t('errors.loadingFilesError');
  } finally {
    loading.value = false;
  }
};

// Grid event handlers
const onGridReady = (params: any) => {
  gridApi.value = params.api;
  gridColumnApi.value = params.columnApi;

  // Auto-size all columns to fit the content
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};

// File deletion handling
const openFile = async (file: any) => {
  if (!props.projectId || !file?.id) {
    error.value = t('errors.invalidFileDownload');
    return;
  }

  try {
    loading.value = true;
    
    // Download the file securely and get a blob URL
    const blobUrl = await DesignService.downloadDesignFile(props.projectId, file.id);
    
    // Store the blob URL securely in session storage (it's temporary anyway)
    sessionStorage.setItem('designFileUrl', blobUrl);
    
    // Navigate to SweetHome3D with secure path parameters
    router.push({
      name: 'sweethome3d',
      params: {
        projectId: props.projectId,
        designId: file.id
      }
    });
  } catch (err: any) {
    console.error('Error opening design file:', err);
    error.value = err.message || t('errors.downloadingFileError');
  } finally {
    loading.value = false;
  }
};

const promptDeleteFile = (file: any) => {
  if (loading.value) return; // Prevent opening delete modal while loading
  fileToDelete.value = file;
  showDeleteConfirmation.value = true;
};

const isDeleting = ref(false);

const confirmDelete = async () => {
  if (!fileToDelete.value || !props.projectId || isDeleting.value) return;

  try {
    isDeleting.value = true;
    loading.value = true;
    await DesignService.deleteProjectDesignFile(String(props.projectId), fileToDelete.value.id);
    showDeleteConfirmation.value = false;
    // Show success notification
    store.dispatch('notifications/showSuccess', t('notifications.fileDeleteSuccess'));    
    // Clear deleted file reference
    fileToDelete.value = null;
    // Refresh file list after deletion
    await fetchFiles(true);
  } catch (err: any) {
    console.error('Error deleting design file:', err);
    error.value = err.message || t('errors.deletingFileError');
    store.dispatch('notifications/showError', error.value);
  } finally {
    loading.value = false;
    isDeleting.value = false;
  }
};

// Cancel delete handler
const cancelDelete = () => {
  showDeleteConfirmation.value = false;
  fileToDelete.value = null;
};

// New design handlers
const handleNewDesignClick = () => {
  if (!props.projectId) {
    error.value = t('notifications.selectFirmFirst');
    return;
  }
  showNewDesignModal.value = true;
};

const handleDesignCreated = async () => {
  showNewDesignModal.value = false;
  // Show success notification
  store.dispatch('notifications/showSuccess', t('design.createSuccess'));
  // Refresh file list after creation
  await fetchFiles(true);
};

const handleNewDesignModalClose = () => {
  showNewDesignModal.value = false;
};

// Handle action clicks from AppDataGrid
const handleAction = (action: any, data: any) => {
  if (action.name === 'open') {
    openFile(data);
  } else if (action.name === 'delete') {
    promptDeleteFile(data);
  }
};

// Window resize handler
const onWindowResize = () => {
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};

// Define emits for parent components to listen to
// const emit = defineEmits(['return-to-project']); // Removed duplicate

// Return to the project details page
const returnToProject = () => {
  // If this component is used inside a project details page, emit an event to parent
  // that will be handled by the ProjectDetails.vue's returnToProject method
  if (props.projectId) {
    emit('return-to-project');
    return;
  }

  // Otherwise navigate back to project (if coming from route)
  if (sourceProject.value && sourceProject.value.id) {
    router.push({
      path: '/project/' + sourceProject.value.id
    });
  } else {
    // Fallback to projects listing if something went wrong
    router.push('/projects');
  }
};

// Component lifecycle hooks
onMounted(async () => {
  // Load files when component mounts if we have a projectId
  if (props.projectId) {
    await fetchFiles();
  } else {
    // show a helpful message when no project is selected
    error.value = t('notifications.selectFirmFirst');
  }

  // Add resize event listener for the grid
  window.addEventListener('resize', onWindowResize);
});

onUnmounted(() => {
  // Remove resize event listener when component is unmounted
  window.removeEventListener('resize', onWindowResize);
});

// Watch for projectId changes
watch(() => props.projectId, async (newId: string | number | null) => {
  if (newId) {
    isInitialLoad.value = true;
    await fetchFiles();
  }
});
</script>

<style scoped>
.file-manager-wrapper {
  width: 100%;
}

.project-design-section {
  width: 100%;
}

/* Project context styling */
.project-context {
  background-color: var(--primary-50);
  border-left: 4px solid var(--primary);
  margin: var(--space-md) 0;
  border-radius: var(--border-radius);
}

.project-context-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-md) var(--space-lg);
}

.project-info h3 {
  margin: 0;
  color: var(--primary-900);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.project-info p {
  margin: var(--space-xs) 0 0;
  color: var(--neutral-700);
}

.return-to-project-btn {
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.return-to-project-btn .btn-icon {
  margin-right: var(--space-xs);
}

.loading-indicator,
.error-message,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-xl);
}

.loading-indicator {
  color: var(--neutral-600);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  text-align: center;
  color: var(--error);
}

.error-message p {
  margin-bottom: var(--space-md);
}

.embedded-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
  padding-bottom: var(--space-sm);
  border-bottom: 1px solid var(--neutral-200);
}

.component-title {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-sm);
}

.btn-icon {
  margin-right: var(--space-xs);
}

.empty-state {
  text-align: center;
}

.empty-state__image {
  max-width: 200px;
  margin-bottom: var(--space-lg);
  opacity: 0.7;
}

.empty-state__title {
  font-size: var(--font-size-xl);
  margin: 0 0 var(--space-md) 0;
}

.empty-state__message {
  color: var(--neutral-600);
  max-width: 400px;
}

/* Filter Panel */
.filter-panel {
  background-color: var(--neutral-50);
  border-radius: var(--border-radius);
  padding: var(--space-md);
  margin-bottom: var(--space-md);
  border: 1px solid var(--neutral-200);
}

.filter-label {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--space-sm);
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.filter-btn {
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--neutral-300);
  background-color: white;
  border-radius: var(--border-radius);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.filter-count {
  background: var(--neutral-100);
  color: var(--neutral-600);
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.75rem;
  min-width: 20px;
  text-align: center;
}

.filter-btn.active .filter-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Table actions & Grid */
.files-container {
  width: 100%;
}

.grid-wrapper {
  border: 1px solid var(--neutral-300);
  border-radius: var(--border-radius);
  height: 500px;
  overflow: hidden;
}

.pagination-info {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: var(--space-md);
  color: var(--neutral-600);
}

/* AG Grid Customizations */
:deep(.files-grid) {
  font-family: var(--font-family);
  height: 100%;
}

:deep(.ag-header-cell-text) {
  font-weight: 600;
  color: var(--text-primary);
}

:deep(.ag-cell) {
  display: flex;
  align-items: center;
  padding: 8px 12px;
}

/* Action buttons */
:deep(.action-buttons) {
  display: flex;
  gap: 0.5rem;
}

:deep(.action-btn) {
  background: transparent;
  color: var(--neutral-700);
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: var(--border-radius-sm);
  transition: all 0.15s ease;
}

:deep(.action-btn:hover) {
  color: var(--primary);
  background-color: var(--neutral-100);
}

:deep(.open-btn:hover) {
  color: var(--primary);
}

:deep(.delete-btn:hover) {
  color: var(--danger);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: var(--border-radius-lg);
  padding: var(--space-lg);
  max-width: 400px;
  width: 100%;
  box-shadow: var(--shadow-lg);
  text-align: center;
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: var(--space-md);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-md);
  margin-top: var(--space-lg);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive styles */
@media (max-width: 768px) {
  .table-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: flex-start;
    width: 100%;
  }

  .filter-panel {
    padding: var(--space-sm);
  }

  .filter-buttons {
    gap: var(--space-xs);
  }

  .filter-btn {
    flex: 1;
    min-width: calc(50% - var(--space-xs));
    justify-content: center;
  }

  .grid-wrapper {
    height: 400px; /* Smaller height on mobile */
  }

  .pagination-info {
    flex-direction: column;
    gap: var(--space-sm);
    align-items: flex-start;
  }
}
</style>
